import * as THREE from 'three';

export class Shield {
  private mesh!: THREE.Group;
  private dome!: THREE.Mesh;
  private runes!: THREE.Points;
  private scene: THREE.Scene;
  private isActive: boolean = true;
  private duration: number;
  private startTime: number;
  private health: number;
  private maxHealth: number;
  private hitEffectTimeout: ReturnType<typeof setTimeout> | null = null;
  private rippleAnimationId: number | null = null;
  private dissolveAnimationId: number | null = null;

  constructor(scene: THREE.Scene, position: THREE.Vector3, health: number, duration: number) {
    this.scene = scene;
    this.health = health;
    this.maxHealth = health;
    this.duration = duration;
    this.startTime = Date.now();

    this.mesh = new THREE.Group();
    this.mesh.position.copy(position);

    this.createDome();
    this.createRunes();
    this.createInnerGlow();

    this.scene.add(this.mesh);
  }

  private createDome(): void {
    const geometry = new THREE.SphereGeometry(1.5, 32, 32, 0, Math.PI * 2, 0, Math.PI / 2);
    const material = new THREE.MeshBasicMaterial({
      color: 0xC9A227,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
      wireframe: false
    });
    this.dome = new THREE.Mesh(geometry, material);
    this.dome.rotation.x = Math.PI;
    this.mesh.add(this.dome);

    const wireGeometry = new THREE.SphereGeometry(1.52, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2);
    const wireMaterial = new THREE.MeshBasicMaterial({
      color: 0xE8C547,
      transparent: true,
      opacity: 0.5,
      wireframe: true
    });
    const wireframe = new THREE.Mesh(wireGeometry, wireMaterial);
    wireframe.rotation.x = Math.PI;
    this.mesh.add(wireframe);
  }

  private createRunes(): void {
    const runeCount = 30;
    const positions = new Float32Array(runeCount * 3);
    const colors = new Float32Array(runeCount * 3);

    for (let i = 0; i < runeCount; i++) {
      const theta = (i / runeCount) * Math.PI * 2;
      const phi = Math.random() * Math.PI / 2;
      const r = 1.45;

      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.cos(phi);
      positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);

      colors[i * 3] = 0.9 + Math.random() * 0.1;
      colors[i * 3 + 1] = 0.7 + Math.random() * 0.2;
      colors[i * 3 + 2] = 0.1 + Math.random() * 0.1;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending
    });

    this.runes = new THREE.Points(geometry, material);
    this.mesh.add(this.runes);
  }

  private createInnerGlow(): void {
    const glowGeometry = new THREE.SphereGeometry(0.8, 16, 16);
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: 0xC9A227,
      transparent: true,
      opacity: 0.15
    });
    const glow = new THREE.Mesh(glowGeometry, glowMaterial);
    glow.position.y = 0.5;
    this.mesh.add(glow);
  }

  update(delta: number): boolean {
    if (!this.isActive) return false;

    const elapsed = Date.now() - this.startTime;
    if (elapsed >= this.duration) {
      this.deactivate();
      return false;
    }

    if (this.runes && this.runes.geometry.attributes.position) {
      this.runes.rotation.y += delta * 0.5;

      const positions = this.runes.geometry.attributes.position.array as Float32Array;
      const runeCount = positions.length / 3;
      for (let i = 0; i < runeCount; i++) {
        const baseY = positions[i * 3 + 1];
        positions[i * 3 + 1] = baseY + Math.sin(Date.now() * 0.003 + i) * 0.01;
      }
      this.runes.geometry.attributes.position.needsUpdate = true;
    }

    if (this.dome) {
      const healthRatio = this.health / this.maxHealth;
      const domeMaterial = this.dome.material as THREE.MeshBasicMaterial;
      domeMaterial.opacity = 0.2 + healthRatio * 0.3;

      const pulseIntensity = 1 + Math.sin(Date.now() * 0.005) * 0.05;
      this.dome.scale.setScalar(pulseIntensity);
    }

    return true;
  }

  takeDamage(amount: number): number {
    const absorbed = Math.min(this.health, amount);
    this.health -= absorbed;

    this.createHitEffect();

    if (this.health <= 0) {
      this.deactivate();
    }

    return amount - absorbed;
  }

  private createHitEffect(): void {
    const flashMaterial = this.dome.material as THREE.MeshBasicMaterial;
    const originalOpacity = flashMaterial.opacity;
    flashMaterial.opacity = 0.8;

    // Clear any existing timeout
    if (this.hitEffectTimeout !== null) {
      clearTimeout(this.hitEffectTimeout);
    }
    this.hitEffectTimeout = setTimeout(() => {
      this.hitEffectTimeout = null;
      if (this.isActive) {
        flashMaterial.opacity = originalOpacity;
      }
    }, 100);

    const rippleGeometry = new THREE.RingGeometry(0.5, 1, 32);
    const rippleMaterial = new THREE.MeshBasicMaterial({
      color: 0xE8C547,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide
    });
    const ripple = new THREE.Mesh(rippleGeometry, rippleMaterial);
    ripple.position.y = 0.8;
    ripple.rotation.x = -Math.PI / 2;
    this.mesh.add(ripple);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;
      ripple.scale.setScalar(1 + elapsed * 3);
      rippleMaterial.opacity = 0.8 - (elapsed / 0.3);

      if (elapsed < 0.3) {
        this.rippleAnimationId = requestAnimationFrame(animate);
      } else {
        this.rippleAnimationId = null;
        this.mesh.remove(ripple);
        rippleGeometry.dispose();
        rippleMaterial.dispose();
      }
    };
    this.rippleAnimationId = requestAnimationFrame(animate);
  }

  private deactivate(): void {
    this.isActive = false;
    this.createDissolveEffect();
  }

  private createDissolveEffect(): void {
    const dissolveParticleCount = 50;
    const positions = new Float32Array(dissolveParticleCount * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < dissolveParticleCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI / 2;
      const r = 1.5;

      positions[i * 3] = this.mesh.position.x + r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = this.mesh.position.y + r * Math.cos(phi);
      positions[i * 3 + 2] = this.mesh.position.z + r * Math.sin(phi) * Math.sin(theta);

      velocities.push(new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        Math.random() * 3 + 1,
        (Math.random() - 0.5) * 2
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0xC9A227,
      size: 0.2,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending
    });

    const dissolve = new THREE.Points(geometry, material);
    this.scene.add(dissolve);

    let elapsed = 0;
    const animate = () => {
      elapsed += 0.016;

      const pos = dissolve.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < dissolveParticleCount; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
      }
      dissolve.geometry.attributes.position.needsUpdate = true;

      material.opacity = 1 - (elapsed / 0.8);

      if (elapsed < 0.8) {
        this.dissolveAnimationId = requestAnimationFrame(animate);
      } else {
        this.dissolveAnimationId = null;
        this.scene.remove(dissolve);
        geometry.dispose();
        material.dispose();
      }
    };

    this.dissolveAnimationId = requestAnimationFrame(animate);
    this.dispose();
  }

  getHealth(): number {
    return this.health;
  }

  isShieldActive(): boolean {
    return this.isActive;
  }

  getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  /** Update shield position to follow a moving wizard */
  setPosition(pos: THREE.Vector3): void {
    this.mesh.position.copy(pos);
  }

  dispose(): void {
    // Cancel any pending animations/timeouts
    if (this.hitEffectTimeout !== null) {
      clearTimeout(this.hitEffectTimeout);
      this.hitEffectTimeout = null;
    }
    if (this.rippleAnimationId !== null) {
      cancelAnimationFrame(this.rippleAnimationId);
      this.rippleAnimationId = null;
    }
    if (this.dissolveAnimationId !== null) {
      cancelAnimationFrame(this.dissolveAnimationId);
      this.dissolveAnimationId = null;
    }
    this.scene.remove(this.mesh);
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Points) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}
